'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  FiSearch,
  FiUser,
  FiBriefcase,
  FiClock,
  FiTerminal,
  FiCode,
  FiFileText,
  FiCopy,
  FiGlobe,
  FiLayers,
  FiZap,
  FiAlertCircle,
  FiCheckCircle,
  FiPlay,
  FiKey,
  FiBookOpen
} from 'react-icons/fi'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import Swal from 'sweetalert2'
import ApiDocumentationModal from '@/components/ApiDocumentationModal'

type ApiConfig = {
  title: string;
  type: 'GET' | 'POST';
  endpoint: string;
  placeholder: string;
  subtitle: string;
  helper: string;
  sampleValue?: string;
  isPost?: boolean;
  defaultBody?: string;
  sampleResponse: string;
  curlSnippet: string;
  jsSnippet: string;
  phpSnippet: string;
  pythonSnippet: string;
}

type RecentItem = {
  id: string;
  tab: string;
  label: string;
  method: 'GET' | 'POST';
  query: string;
  status: 'success' | 'error';
  createdAt: string;
}

const STORAGE_KEY = 'miapi-identity-recent-tests'

const API_CONFIGS: Record<string, ApiConfig> = {
  dni: {
    title: 'Consulta de datos DNI',
    type: 'GET',
    endpoint: 'https://miapi.cloud/v1/dni/',
    placeholder: 'Número de DNI',
    subtitle: 'Consulta datos personales usando un DNI válido de 8 dígitos.',
    helper: 'Usa solo números. Ideal para autocompletar formularios o validar identidad.',
    sampleValue: '70135060',
    sampleResponse: `{
  "success": true,
  "datos": {
    "dni": "70135060",
    "nombres": "YOVANA LISBETH",
    "ape_paterno": "MAMANI",
    "ape_materno": "FAIJO",
    "fecha_nacimiento": "16/04/1994",
    "edad": 32,
    "domiciliado": {
      "direccion": "COMUNID. SANTA ROSA DE UYUNI",
      "distrito": "QUILCAPUNCU",
      "provincia": "SAN ANTONIO DE PUTINA",
      "departamento": "PUNO",
      "ubigeo": "211004"
    }
  }
}`,
    curlSnippet: `curl -X GET "https://miapi.cloud/v1/dni/12345678" \\
  -H "Authorization: Bearer TU_TOKEN_AQUI"`,
    jsSnippet: `const response = await fetch('https://miapi.cloud/v1/dni/12345678', {
  headers: { Authorization: 'Bearer TU_TOKEN_AQUI' }
})
console.log(await response.json())`,
    phpSnippet: `$ch = curl_init('https://miapi.cloud/v1/dni/12345678');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ['Authorization: Bearer TU_TOKEN_AQUI']);
echo curl_exec($ch);
curl_close($ch);`,
    pythonSnippet: `import requests
response = requests.get(
    'https://miapi.cloud/v1/dni/12345678',
    headers={'Authorization': 'Bearer TU_TOKEN_AQUI'}
)
print(response.json())`
  },
  ruc: {
    title: 'Consulta de datos RUC',
    type: 'GET',
    endpoint: 'https://miapi.cloud/v1/ruc/',
    placeholder: 'Número de RUC',
    subtitle: 'Consulta el estado tributario básico de una empresa con RUC.',
    helper: 'Usa un RUC de 11 dígitos para validar razón social, estado y condición.',
    sampleValue: '20605100016',
    sampleResponse: `{
  "success": true,
  "datos": {
    "ruc": "20605100016",
    "razon_social": "RVM MAQUINARIAS S.A.C.",
    "estado": "ACTIVO",
    "condicion": "HABIDO",
    "domiciliado": {
      "direccion": "NICOLAS AYLLON NRO. 8510 DPTO. 804",
      "distrito": "ATE",
      "provincia": "LIMA",
      "departamento": "LIMA",
      "ubigeo": "150103"
    }
  }
}`,
    curlSnippet: `curl -X GET "https://miapi.cloud/v1/ruc/20123456789" \\
  -H "Authorization: Bearer TU_TOKEN_AQUI"`,
    jsSnippet: `const response = await fetch('https://miapi.cloud/v1/ruc/20123456789', {
  headers: { Authorization: 'Bearer TU_TOKEN_AQUI' }
})
console.log(await response.json())`,
    phpSnippet: `$ch = curl_init('https://miapi.cloud/v1/ruc/20123456789');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ['Authorization: Bearer TU_TOKEN_AQUI']);
echo curl_exec($ch);
curl_close($ch);`,
    pythonSnippet: `import requests
response = requests.get(
    'https://miapi.cloud/v1/ruc/20123456789',
    headers={'Authorization': 'Bearer TU_TOKEN_AQUI'}
)
print(response.json())`
  },
  cpe: {
    title: 'Validación de Comprobantes (CPE)',
    type: 'POST',
    endpoint: 'https://miapi.cloud/v1/validarcpe',
    placeholder: '',
    subtitle: 'Valida comprobantes electrónicos enviados a SUNAT.',
    helper: 'Envía un JSON válido con los campos obligatorios del comprobante.',
    isPost: true,
    defaultBody: JSON.stringify({
      numRuc: "20608530291",
      codComp: "01",
      numeroSerie: "F005",
      numero: "1567",
      fechaEmision: "24/09/2026",
      monto: "504.00",
      codDocRecep: "6",
      numDocRecep: "20615138356"
    }, null, 4),
    sampleResponse: `{
  "data": {
    "numero": "1567",
    "numeroSerie": "F005",
    "codComp": "01",
    "fechaEmision": "24/09/2026",
    "numRuc": "20608530291",
    "codDocRecep": "6",
    "numDocRecep": "20615138356",
    "monto": "504.00",
    "estadoCp": "1",
    "estadoRuc": "00",
    "condDomiRuc": "00",
    "observaciones": [],
    "estadoCpDesc": "ACEPTADO",
    "estadoCpDetalle": "ACEPTADO (Válido)",
    "estadoRucDesc": "ACTIVO",
    "condDomiRucDesc": "HABIDO",
    "codCompDesc": "Factura",
    "codDocRecepDesc": "RUC",
    "estadoComprobanteTexto": "Estado del comprobante a la fecha de la consulta : ACEPTADO",
    "estadoContribuyenteTexto": "Estado del contribuyente a la fecha de emisión : ACTIVO",
    "condicionDomicilioTexto": "Condición de domicilio a la fecha de emisión : HABIDO",
    "desglose": {
      "estadoComprobante": "estadoCp: '1' (ACEPTADO)",
      "estadoContribuyente": "estadoRuc: '00' (ACTIVO)",
      "condicionDomicilio": "condDomiRuc: '00' (HABIDO)",
      "tipoComprobante": "codComp: '01' (Factura)",
      "tipoDocReceptor": "codDocRecep: '6' (RUC)"
    }
  },
  "rpta": 1
}`,
    curlSnippet: `curl -X POST "https://miapi.cloud/v1/validarcpe" \\
  -H "Authorization: Bearer TU_TOKEN_AQUI" \\
  -H "Content-Type: application/json" \\
  -d '{"numRuc":"20608530291","codComp":"01","numeroSerie":"F005","numero":"1567","fechaEmision":"24/09/2026","monto":"504.00","codDocRecep":"6","numDocRecep":"20615138356"}'`,
    jsSnippet: `const payload = {
  numRuc: '20608530291',
  codComp: '01',
  numeroSerie: 'F005',
  numero: '1567',
  fechaEmision: '24/09/2026',
  monto: '504.00',
  codDocRecep: '6',
  numDocRecep: '20615138356'
}

const response = await fetch('https://miapi.cloud/v1/validarcpe', {
  method: 'POST',
  headers: {
    Authorization: 'Bearer TU_TOKEN_AQUI',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify(payload)
})
console.log(await response.json())`,
    phpSnippet: `$payload = [
  'numRuc' => '20608530291',
  'codComp' => '01',
  'numeroSerie' => 'F005',
  'numero' => '1567',
  'fechaEmision' => '24/09/2026',
  'monto' => '504.00',
  'codDocRecep' => '6',
  'numDocRecep' => '20615138356'
];

$ch = curl_init('https://miapi.cloud/v1/validarcpe');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  'Authorization: Bearer TU_TOKEN_AQUI',
  'Content-Type': 'application/json'
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($payload));
echo curl_exec($ch);
curl_close($ch);`,
    pythonSnippet: `import requests
payload = {
    'numRuc': '20608530291',
    'codComp': '01',
    'numeroSerie': 'F005',
    'numero': '1567',
    'fechaEmision': '24/09/2026',
    'monto': '504.00',
    'codDocRecep': '6',
    'numDocRecep': '20615138356'
}
response = requests.post(
    'https://miapi.cloud/v1/validarcpe',
    headers={
        'Authorization': 'Bearer TU_TOKEN_AQUI',
        'Content-Type': 'application/json'
    },
    json=payload
)
print(response.json())`
  },
  rucdetallado: {
    title: 'Consulta RUC Detallado',
    type: 'GET',
    endpoint: 'https://miapi.cloud/v1/ruc/det/',
    placeholder: 'Número de RUC',
    subtitle: 'Consulta información ampliada del RUC.',
    helper: 'Útil para integraciones que requieren datos empresariales más completos.',
    sampleValue: '20605100016',
    sampleResponse: `{
  "datos": {
    "razon_social": "RVM MAQUINARIAS S.A.C.",
    "estado": "ACTIVO",
    "condicion": "HABIDO",
    "ubicacion": {
      "codUbigeo": "150103",
      "direccion": "---- NICOLAS AYLLON NRO. 8510    DPTO. 804",
      "departamento": "LIMA ",
      "provincia": "LIMA ",
      "distrito": "ATE"
    },
    "contacto": {
      "numTelefono1": "-",
      "numTelefono2": "-",
      "numTelefono3": "943098820"
    },
    "correos": {
      "codCorreo1": "motomaquinaria@hotmail.com",
      "codCorreo2": "-"
    }
  }
}`,
    curlSnippet: `curl -X GET "https://miapi.cloud/v1/ruc/det/20123456789" \\
  -H "Authorization: Bearer TU_TOKEN_AQUI"`,
    jsSnippet: `const response = await fetch('https://miapi.cloud/v1/ruc/det/20123456789', {
  headers: { Authorization: 'Bearer TU_TOKEN_AQUI' }
})
console.log(await response.json())`,
    phpSnippet: `$ch = curl_init('https://miapi.cloud/v1/ruc/det/20123456789');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ['Authorization: Bearer TU_TOKEN_AQUI']);
echo curl_exec($ch);
curl_close($ch);`,
    pythonSnippet: `import requests
response = requests.get(
    'https://miapi.cloud/v1/ruc/det/20123456789',
    headers={'Authorization': 'Bearer TU_TOKEN_AQUI'}
)
print(response.json())`
  },
  ruccompleto: {
    title: 'Consulta RUC Completo',
    type: 'GET',
    endpoint: 'https://miapi.cloud/v1/ruc/completo/',
    placeholder: 'Número de RUC',
    subtitle: 'Obtén la respuesta más completa disponible del RUC.',
    helper: 'Recomendado para flujos empresariales avanzados y validaciones profundas.',
    sampleValue: '20605100016',
    sampleResponse: `{
  "success": true,
  "datos": {
    "ruc": "20605100016",
    "razon_social": "RVM MAQUINARIAS S.A.C.",
    "tipo_contribuyente": "SOCIEDAD ANONIMA CERRADA",
    "nombre_comercial": "RVM MAQUINARIAS",
    "fecha_inscripcion": "08/08/2019",
    "fecha_inicio_actividades": "08/08/2019",
    "estado": "ACTIVO",
    "fecha_baja": "",
    "mensaje_estado": null,
    "condicion": "HABIDO",
    "domicilio_fiscal": {
      "direccion": "NICOLAS AYLLON NRO. 8510 DPTO. 804 (BLOCK 2)",
      "distrito": "ATE",
      "provincia": "LIMA",
      "departamento": "LIMA"
    },
    "sistema_emision": "MANUAL/COMPUTARIZADO",
    "actividad_comercio_exterior": "SIN ACTIVIDAD",
    "sistema_contabilidad": "MANUAL/COMPUTARIZADO",
    "actividades_economicas": [
      "Principal    - 4520 - MANTENIMIENTO Y REPARACIÓN DE VEHÍCULOS AUTOMOTORES",
      "Secundaria 1 - 4510  - VENTA DE VEHÍCULOS AUTOMOTORES",
      "Secundaria 2 - 4530 - VENTA DE PARTES, PIEZAS Y ACCESORIOS PARA VEHÍCULOS AUTOMOTORES"
    ],
    "comprobantes_pago": [
      "NINGUNO"
    ],
    "sistema_emision_electronica": [
      "FACTURA PORTAL                      DESDE 14/10/2019",
      "DESDE LOS SISTEMAS DEL CONTRIBUYENTE. AUTORIZ DESDE 05/05/2021",
      "GUIA DE REMISION                    DESDE 12/05/2021"
    ],
    "emisor_electronico_desde": "14/10/2019",
    "comprobantes_electronicos": [
      "FACTURA (desde 14/10/2019)",
      "GUIA (desde 29/11/2019)",
      "BOLETA (desde 05/05/2021)"
    ],
    "afiliado_PLE_desde": "-",
    "padrones": [
      "Incorporado al Régimen de Buenos Contribuyentes (Resolución N° 0230050316956) a partir del 01/02/2022"
    ],
    "agente_retencion": "NO",
    "agente_percepcion": "NO"
  }
}`,
    curlSnippet: `curl -X GET "https://miapi.cloud/v1/ruc/completo/20123456789" \\
  -H "Authorization: Bearer TU_TOKEN_AQUI"`,
    jsSnippet: `const response = await fetch('https://miapi.cloud/v1/ruc/completo/20123456789', {
  headers: { Authorization: 'Bearer TU_TOKEN_AQUI' }
})
console.log(await response.json())`,
    phpSnippet: `$ch = curl_init('https://miapi.cloud/v1/ruc/completo/20123456789');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ['Authorization: Bearer TU_TOKEN_AQUI']);
echo curl_exec($ch);
curl_close($ch);`,
    pythonSnippet: `import requests
response = requests.get(
    'https://miapi.cloud/v1/ruc/completo/20123456789',
    headers={'Authorization': 'Bearer TU_TOKEN_AQUI'}
)
print(response.json())`
  }
}

export default function IdentidadPage() {
  const [activeTab, setActiveTab] = useState('dni')
  const [token, setToken] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [docNumber, setDocNumber] = useState('')
  const [requestBody, setRequestBody] = useState('')
  const [response, setResponse] = useState<any>(null)
  const [responseTime, setResponseTime] = useState<string | null>(null)
  const [showDocModal, setShowDocModal] = useState(false)
  const [recentTests, setRecentTests] = useState<RecentItem[]>([])
  const [activeSnippet, setActiveSnippet] = useState<'curl' | 'js' | 'php' | 'python'>('curl')

  useEffect(() => {
    fetchToken()
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY)
      if (saved) setRecentTests(JSON.parse(saved))
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    // Reset inputs when tab changes
    setDocNumber('')
    setResponse(null)
    setResponseTime(null)
    setActiveSnippet('curl')
    if (API_CONFIGS[activeTab].isPost) {
      setRequestBody(API_CONFIGS[activeTab].defaultBody || '')
    } else {
      setRequestBody('')
    }
  }, [activeTab])

  const fetchToken = async () => {
    try {
      const res = await fetch('/api/user/token')
      const data = await res.json()
      if (data.success) setToken(data.token)
    } catch (e) {
      console.error(e)
    }
  }

  const validateInput = () => {
    if (activeTab === 'dni' && !/^\d{8}$/.test(docNumber.trim())) {
      return 'El DNI debe tener exactamente 8 dígitos.'
    }

    if (['ruc', 'rucdetallado', 'ruccompleto'].includes(activeTab) && !/^\d{11}$/.test(docNumber.trim())) {
      return 'El RUC debe tener exactamente 11 dígitos.'
    }

    if (activeTab === 'cpe') {
      try {
        const parsed = JSON.parse(requestBody)
        const rucVal = parsed?.numRuc || parsed?.ruc
        const codCompVal = parsed?.codComp || parsed?.tipo_documento
        const serieVal = parsed?.numeroSerie || parsed?.serie
        const numeroVal = parsed?.numero
        const fechaVal = parsed?.fechaEmision || parsed?.fecha_emision
        const montoVal = parsed?.monto

        if (!rucVal || !codCompVal || !serieVal || !numeroVal || !fechaVal || !montoVal) {
          return 'Faltan campos obligatorios en el JSON (numRuc, codComp, numeroSerie, numero, fechaEmision, monto).'
        }
        if (!/^\d{11}$/.test(String(rucVal))) {
          return 'El campo RUC del emisor debe tener 11 dígitos.'
        }
      } catch {
        return 'El cuerpo JSON no es válido.'
      }
    }

    return null
  }

  const persistRecentTest = (item: RecentItem) => {
    const next = [item, ...recentTests.filter((entry) => !(entry.tab === item.tab && entry.query === item.query))].slice(0, 5)
    setRecentTests(next)
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch (error) {
      console.error(error)
    }
  }

  const handleSearch = async () => {
    const config = API_CONFIGS[activeTab]

    const validationError = validateInput()
    if (validationError) {
      return Swal.fire('Validación', validationError, 'warning')
    }
    if (!token) {
      return Swal.fire('Error', 'Ingresa un token de acceso', 'error')
    }

    setLoading(true)
    setResponse(null)
    setResponseTime(null)

    const startTime = performance.now()
    const url = config.isPost ? config.endpoint : `${config.endpoint}${docNumber}`

    try {
      const options: RequestInit = {
        method: config.type,
        headers: {
          'Authorization': `Bearer ${token.trim()}`,
          'Content-Type': 'application/json'
        }
      }

      if (config.isPost) {
        options.body = requestBody
      }

      const res = await fetch(url, { ...options })
      const data = await res.json()
      const endTime = performance.now()

      const timeSecs = (endTime - startTime) / 1000
      setResponseTime(timeSecs < 1 ? `${(timeSecs * 1000).toFixed(0)}ms` : `${timeSecs.toFixed(2)}s`)
      setResponse(data)
      persistRecentTest({
        id: `${Date.now()}`,
        tab: activeTab,
        label: config.title,
        method: config.type,
        query: config.isPost ? 'JSON enviado' : docNumber.trim(),
        status: res.ok ? 'success' : 'error',
        createdAt: new Date().toISOString()
      })

      // SI la respuesta fue exitosa (res.ok), registrar el consumo internamente
      if (res.ok) {
        fetch('/api/user/record', { method: 'POST' }).catch(err => console.error('Error tracking usage:', err))
      }
    } catch (e: any) {
      setResponse({ error: 'Error al conectar con la API', details: e.message })
      persistRecentTest({
        id: `${Date.now()}`,
        tab: activeTab,
        label: config.title,
        method: config.type,
        query: config.isPost ? 'JSON enviado' : docNumber.trim(),
        status: 'error',
        createdAt: new Date().toISOString()
      })
    } finally {
      setLoading(false)
    }
  }

  const copyResponse = () => {
    if (!response) return
    navigator.clipboard.writeText(JSON.stringify(response, null, 2))
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Copiado al portapapeles',
      showConfirmButton: false,
      timer: 2000
    })
  }

  const copySnippet = async (content: string) => {
    await navigator.clipboard.writeText(content)
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Código copiado',
      showConfirmButton: false,
      timer: 1800
    })
  }

  const handleQuickLoad = (item: RecentItem) => {
    setActiveTab(item.tab as typeof activeTab)
    if (item.tab !== 'cpe') {
      setDocNumber(item.query)
    }
  }

  const responseOk = response && (response.success === true || response.estado === 'success' || response.rpta === 1)
  const currentSnippet = {
    curl: API_CONFIGS[activeTab].curlSnippet,
    js: API_CONFIGS[activeTab].jsSnippet,
    php: API_CONFIGS[activeTab].phpSnippet,
    python: API_CONFIGS[activeTab].pythonSnippet
  }[activeSnippet]

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-gray-900">Validación de Identidad</h1>
        <p className="text-gray-500">Consulta datos de personas y empresas en tiempo real desde nuestras APIS.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-1">
        <Card className="border-none shadow-sm">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-50 text-violet-600">
              <FiBookOpen className="h-7 w-7" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Modo actual</p>
              <p className="mt-1 text-base font-semibold text-slate-900">Probador técnico</p>
              <p className="mt-1 text-sm text-slate-500">Validaciones, snippets y respuestas listas para integrar.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-xl mb-6 grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-nowrap h-auto gap-1">
          <TabsTrigger value="dni" className="rounded-lg px-2 sm:px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center justify-center lg:justify-start gap-2 text-xs sm:text-sm">
            <FiUser className="h-4 w-4" /> DNI
          </TabsTrigger>
          <TabsTrigger value="ruc" className="rounded-lg px-2 sm:px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center justify-center lg:justify-start gap-2 text-xs sm:text-sm">
            <FiBriefcase className="h-4 w-4" /> RUC
          </TabsTrigger>
          <TabsTrigger value="cpe" className="rounded-lg px-2 sm:px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center justify-center lg:justify-start gap-2 text-xs sm:text-sm">
            <FiLayers className="h-4 w-4" /> <span className="hidden sm:inline">Validación</span> CPE
          </TabsTrigger>
          <TabsTrigger value="rucdetallado" className="rounded-lg px-2 sm:px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center justify-center lg:justify-start gap-2 text-xs sm:text-sm">
            <FiGlobe className="h-4 w-4" /> <span className="hidden sm:inline">RUC</span> Detallado
          </TabsTrigger>
          <TabsTrigger value="ruccompleto" className="rounded-lg px-2 sm:px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center justify-center lg:justify-start gap-2 text-xs sm:text-sm">
            <FiZap className="h-4 w-4" /> <span className="hidden sm:inline">RUC</span> Completo
          </TabsTrigger>
        </TabsList>

        {Object.entries(API_CONFIGS).map(([key, config]) => (
          <TabsContent key={key} value={key} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-none shadow-sm h-full">
                <CardHeader className="bg-gray-50/50 border-b py-4">
                  <CardTitle className="text-sm font-bold flex flex-col gap-1">
                    <span>{config.title}</span>
                    <span className="text-xs font-normal text-slate-500 leading-5">
                      {config.subtitle}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100/50 text-xs text-blue-800 leading-relaxed">
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-blue-500 mb-2">Cómo probar este endpoint</p>
                    El endpoint <code>{config.endpoint}</code> es fijo.
                    {!config.isPost ? ' Solo debes ingresar el parámetro en el campo correspondiente.' : ' Debes enviar el cuerpo de la solicitud en formato JSON.'}
                    <div className="mt-2 text-blue-900">{config.helper}</div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-3">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Endpoint</label>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-0">
                        <div className="flex flex-1">
                          <span className="hidden sm:flex px-4 py-2.5 bg-gray-100 border border-gray-200 border-r-0 rounded-l-xl text-xs items-center text-gray-500 font-mono">
                            URL
                          </span>
                          <input
                            type="text"
                            readOnly
                            value={config.endpoint}
                            className="flex-1 px-4 py-3 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-xl sm:rounded-none sm:rounded-l-none text-[11px] sm:text-xs font-mono text-gray-500 focus:outline-none"
                          />
                        </div>
                        {!config.isPost && (
                          <input
                            type="text"
                            placeholder={config.placeholder}
                            value={docNumber}
                            onChange={(e) => setDocNumber(e.target.value.replace(/\s+/g, ''))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSearch()
                            }}
                            className="w-full sm:w-1/3 px-4 py-3 sm:py-2.5 bg-white border border-gray-200 sm:border-l-0 rounded-xl sm:rounded-l-none sm:rounded-r-xl text-sm sm:text-xs font-mono focus:ring-2 focus:ring-blue-500 focus:outline-none border-blue-200 sm:border-gray-200"
                          />
                        )}
                      </div>
                      {!config.isPost && config.sampleValue && (
                        <button
                          type="button"
                          onClick={() => setDocNumber(config.sampleValue || '')}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                        >
                          Cargar ejemplo
                        </button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Bearer Token de Acceso</label>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-0">
                        <span className="hidden sm:flex px-4 py-2.5 bg-gray-100 border border-gray-200 border-r-0 rounded-l-xl text-xs items-center text-gray-500 font-mono">
                          Bearer
                        </span>
                        <input
                          type="text"
                          value={token}
                          onChange={(e) => setToken(e.target.value)}
                          placeholder="Ingresa tu token"
                          className="flex-1 px-4 py-3 sm:py-2.5 bg-white border border-gray-200 rounded-xl sm:rounded-l-none sm:rounded-r-xl text-sm sm:text-xs font-mono focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                      </div>
                    </div>

                    {config.isPost && (
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Cuerpo de la Solicitud (JSON)</label>
                        <textarea
                          value={requestBody}
                          onChange={(e) => setRequestBody(e.target.value)}
                          rows={8}
                          className="w-full px-4 py-3 bg-slate-900 text-green-400 font-mono text-xs rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setRequestBody(config.defaultBody || '')}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                        >
                          Restaurar ejemplo
                        </button>
                      </div>
                    )}

                    <Button
                      onClick={handleSearch}
                      disabled={loading}
                      className="w-full bg-blue-600 hover:bg-blue-700 py-6 rounded-xl font-bold shadow-lg shadow-blue-100 gap-2"
                    >
                      {loading ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Probar API'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm min-h-[450px] flex flex-col overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between border-b py-4 bg-gray-50/50">
                  <div>
                    <CardTitle className="text-sm font-bold flex items-center gap-2 text-gray-700">
                      <FiCode className="text-purple-500" /> Respuesta de la API
                    </CardTitle>
                    <p className="mt-1 text-xs text-slate-500">Lee la respuesta técnica y copia el JSON si lo necesitas.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {responseTime && (
                      <span className="text-[10px] font-bold text-gray-400 bg-white px-2 py-1 rounded-md border flex items-center gap-1">
                        <FiClock className="h-3 w-3" /> {responseTime}
                      </span>
                    )}
                    <button onClick={copyResponse} className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                      <FiCopy className="h-4 w-4" />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 p-0 overflow-hidden relative bg-[#1e1e1e]">
                  <div className="border-b border-slate-800 px-6 py-4 bg-slate-950/70">
                    <div className={`rounded-xl border px-4 py-3 ${response ? (responseOk ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700') : 'border-slate-700 bg-slate-900 text-slate-300'}`}>
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {response ? (responseOk ? <FiCheckCircle className="h-4 w-4" /> : <FiAlertCircle className="h-4 w-4" />) : <FiPlay className="h-4 w-4" />}
                        </div>
                        <div>
                          <p className="text-sm font-bold">
                            {response ? (responseOk ? 'Respuesta exitosa' : 'Respuesta con error') : 'Sin ejecutar'}
                          </p>
                          <p className="mt-1 text-xs">
                            {response ? (response?.error || response?.message || 'La API respondió correctamente.') : 'Aquí aparecerá el resultado de tu prueba.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-[86px] left-0 w-full h-[calc(100%-86px)] p-6 font-mono text-sm overflow-auto text-green-400 scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
                    {response ? (
                      <pre className="whitespace-pre-wrap text-[11px] sm:text-sm">{JSON.stringify(response, null, 2)}</pre>
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-gray-600">
                        <FiTerminal className="h-12 w-12 mb-4 opacity-10" />
                        <p className="text-[10px] uppercase tracking-[0.2em]">Aquí aparecerá la respuesta de la API...</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr,0.9fr]">
        <Card className="border-none shadow-sm overflow-hidden">
          <CardHeader className="border-b bg-gray-50/50">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-sm font-bold flex items-center gap-2 text-gray-700">
                  <FiFileText className="text-blue-500" /> Ejemplos de Integración
                </CardTitle>
                <p className="mt-1 text-sm text-slate-500">Copia el snippet del endpoint activo en el lenguaje que prefieras.</p>
              </div>
              <button
                type="button"
                onClick={() => copySnippet(currentSnippet)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
              >
                Copiar código
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-5">
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'curl', label: 'cURL' },
                { key: 'js', label: 'JavaScript' },
                { key: 'php', label: 'PHP' },
                { key: 'python', label: 'Python' }
              ].map((snippet) => (
                <button
                  key={snippet.key}
                  onClick={() => setActiveSnippet(snippet.key as typeof activeSnippet)}
                  className={`rounded-full px-3 py-2 text-xs font-bold transition ${activeSnippet === snippet.key
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                >
                  {snippet.label}
                </button>
              ))}
            </div>

            <pre className="overflow-auto rounded-[22px] bg-slate-950 p-5 text-[11px] text-emerald-400 ring-1 ring-slate-800 sm:text-sm">
              <code>{currentSnippet}</code>
            </pre>

            <div className="rounded-[22px] border border-slate-100 bg-slate-50 p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Respuesta esperada</p>
              <pre className="mt-3 overflow-auto whitespace-pre-wrap rounded-2xl bg-white p-4 text-[11px] text-slate-700 ring-1 ring-slate-100 sm:text-sm">
                <code>{API_CONFIGS[activeTab].sampleResponse}</code>
              </pre>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-none shadow-sm">
            <CardHeader className="border-b bg-gray-50/50">
              <CardTitle className="text-sm font-bold text-gray-700">Últimas pruebas</CardTitle>
            </CardHeader>
            <CardContent className="pt-5">
              <div className="space-y-3">
                {recentTests.length > 0 ? (
                  recentTests.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleQuickLoad(item)}
                      className="w-full rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-left transition hover:border-slate-200 hover:bg-white"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                          <p className="mt-1 truncate text-xs text-slate-500">{item.query}</p>
                        </div>
                        <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${item.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="mt-2 text-[11px] text-slate-400">
                        {item.method} · {new Date(item.createdAt).toLocaleString('es-PE')}
                      </p>
                    </button>
                  ))
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                    Aún no hiciste pruebas en este navegador.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm">
            <CardHeader className="border-b bg-gray-50/50">
              <CardTitle className="text-sm font-bold text-gray-700">Errores comunes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-5">
              {[
                { code: '401', text: 'Token inválido o no enviado.' },
                { code: '404', text: 'Documento no encontrado.' },
                { code: '429', text: 'Límite de consultas excedido.' },
                { code: '500', text: 'Error interno o proveedor no disponible.' }
              ].map((item) => (
                <div key={item.code} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                  <span className="rounded-xl bg-red-100 px-2 py-1 text-[10px] font-bold text-red-700">{item.code}</span>
                  <span className="text-sm text-slate-600">{item.text}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="bg-slate-900 p-8 rounded-[2rem] border border-slate-800 flex flex-col md:flex-row items-center gap-8 shadow-2xl relative overflow-hidden group">
        <div className="absolute top-0 right-0 p-10 opacity-10 group-hover:opacity-20 transition-opacity">
          <FiCode className="h-40 w-40 text-blue-500 rotate-12" />
        </div>
        <div className="h-16 w-16 bg-blue-600/20 rounded-2xl flex items-center justify-center shrink-0 border border-blue-500/30">
          <FiFileText className="h-8 w-8 text-blue-500" />
        </div>
        <div className="relative z-10">
          <h4 className="font-bold text-white text-lg">Documentación Técnica</h4>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed max-w-2xl">
            Todas nuestras APIs siguen el estándar REST. Utiliza el endpoint base
            <code className="mx-1 bg-slate-800 px-1.5 py-0.5 rounded text-blue-400">https://miapi.cloud/v1/</code>
            y autentica tus peticiones mediante el encabezado
            <code className="mx-1 bg-slate-800 px-1.5 py-0.5 rounded text-purple-400">Authorization: Bearer [TOKEN]</code>.
          </p>
        </div>
        <Button
          onClick={() => setShowDocModal(true)}
          className="md:ml-auto bg-white text-slate-900 hover:bg-slate-200 rounded-xl relative z-10 font-bold px-6 shadow-xl"
        >
          Ver Documentación
        </Button>
      </div>

      <ApiDocumentationModal open={showDocModal} onOpenChange={setShowDocModal} />
    </div>
  )
}
